// Create a PWA in React with TypeScript that allows:
// 1. Scanning barcodes and Data Matrix using the camera (zxing-js).
// 2. Detecting whether a given serial/product already exists in localStorage.
// 3. If it does not exist: ask user for name and quantity, then add to DB.
// 4. If it exists: ask whether to add more or consume the product.
// 5. Notify if a product quantity is < 5 units.
// 6. Ensure PWA: manifest, service worker, mobile-friendly UI.
// 7. Components: Scanner, ProductForm, Inventory, Notifications.


import React, { useState, useEffect } from 'react';
import './styles/main.scss';
import Scanner from './components/Scanner';
import ProductForm from './components/ProductForm';
import Inventory from './components/Inventory';
import Modal from './components/Modal';
import ProductActionModal from './components/ProductActionModal';
import useProductManager from './hooks/useProductManager';
import { Product } from './types';
import { getLowStockProducts, getInventory } from './utils/storage';
import SvgIcon from './utils/svgIcons';
import Notifications from './components/Notifications';
import Info from './components/Info';

type TabType = 'scan' | 'inventory' | 'info' | 'notifications';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('scan');
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);
  
  const {
    currentProduct,
    isProductFormOpen,
    scanResult,
    isActionModalOpen,
    computedProductId,
    initialProductData,
    expiryEditable,
    handleScanSuccess,
    handleProductSubmit,
    openEditForm,
    closeProductForm,
    closeActionModal,
    handleProductAction
  } = useProductManager();

  // Check for low stock products
  useEffect(() => {
    const checkLowStockProducts = () => {
      const products = getLowStockProducts();
      setLowStockProducts(products);
    };
    
    checkLowStockProducts();
    
    // Check every 30 seconds
    const interval = setInterval(checkLowStockProducts, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleProductQuantityAction = (action: 'increase' | 'decrease', id: string, amount: number = 1) => {
    handleProductAction(action, id, amount);
    if (selectedProduct) {
      setNotificationModalOpen(false);
    }
  };

  const handleViewProduct = (product: Product) => {
    setSelectedProduct(product);
    setNotificationModalOpen(true);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'scan':
        return <Scanner onScanSuccess={handleScanSuccess} fullscreen={true} />;
      case 'inventory':
        return <Inventory onEditProduct={openEditForm} />;
      case 'notifications':
        return <Notifications onViewProduct={handleViewProduct} lowStockProducts={lowStockProducts} />;
      case 'info':
        return <Info />;
      default:
        return <Scanner onScanSuccess={handleScanSuccess} fullscreen={true} />;
    }
  };

  // Low stock notifications count
  const notificationsCount = lowStockProducts.length;

  return (
    <div className="App">
      <header className="App-header">
        {activeTab === 'scan' ? (
          <SvgIcon name="logo" className="App-logo" size={120} />
        ) : (
          <SvgIcon name="logo-dark" className="App-logo" size={120} />
        )}
      </header>

      <main className="App-content">
        {renderContent()}
      </main>

      <nav className="bottom-nav">
        <button 
          className={`nav-item ${activeTab === 'scan' ? 'active' : ''}`}
          onClick={() => setActiveTab('scan')}
        >
          <SvgIcon name="camera" size={28} className="nav-icon" />
          <span className="nav-label">Scan</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'inventory' ? 'active' : ''}`}
          onClick={() => setActiveTab('inventory')}
        >
          <SvgIcon name="box" size={28} className="nav-icon" />
          <span className="nav-label">Stock</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <SvgIcon name="info" size={28} className="nav-icon" />
          <span className="nav-label">Info</span>
        </button>
        <button 
          className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          <SvgIcon name="bell" size={28} className="nav-icon" />
          {notificationsCount > 0 && (
            <span className="nav-badge">{notificationsCount}</span>
          )}
          <span className="nav-label">
            Notifications
          </span>
        </button>
      </nav>

      {/* Product Form Modal */}
      <Modal isOpen={isProductFormOpen} onClose={closeProductForm}>
        <ProductForm
          productId={computedProductId || scanResult?.text}
          initialData={initialProductData || currentProduct || {}}
          onSubmit={handleProductSubmit}
          onCancel={closeProductForm}
          expiryEditable={expiryEditable}
          existingCategories={Array.from(
            new Set(
              (getInventory()
                .map(p => p.category)
                .filter((c): c is string => !!c)) as string[]
            )
          )}
        />
      </Modal>

      {/* Product Action Modal - after scanning an existing product */}
      {currentProduct && (
        <ProductActionModal
          isOpen={isActionModalOpen}
          onClose={closeActionModal}
          product={currentProduct}
          onIncrease={(id, amount) => handleProductQuantityAction('increase', id, amount)}
          onDecrease={(id, amount) => handleProductQuantityAction('decrease', id, amount)}
        />
      )}

      {/* Product Action Modal - from notifications */}
      {selectedProduct && (
        <ProductActionModal
          isOpen={notificationModalOpen}
          onClose={() => setNotificationModalOpen(false)}
          product={selectedProduct}
          onIncrease={(id, amount) => handleProductQuantityAction('increase', id, amount)}
          onDecrease={(id, amount) => handleProductQuantityAction('decrease', id, amount)}
        />
      )}
    </div>
  );
}

export default App;
